'use client';

import { getCookie } from '@akinon/next/utils';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@theme/redux/store';

// Define interface for price alert
export interface PriceAlertItem {
  pk: number;
  product: number;
}

export interface PriceAlert {
  count: number;
  next: string | null;
  previous: string | null;
  results: PriceAlertItem[];
}

export interface PriceAlertState {
  alerts: PriceAlert | null;
  isLoading: boolean;
  isSuccess: boolean;
  error: string | null;
}

const initialState: PriceAlertState = {
  alerts: null,
  isLoading: false,
  isSuccess: false,
  error: null
};

export const createPriceAlert = createAsyncThunk(
  'priceAlert/create',
  async (product: string | number, { rejectWithValue }) => {
    const csrftoken = getCookie('csrftoken');

    const productId = String(product);

    try {
      const formData = new FormData();
      formData.append('product', productId);

      const response = await fetch('/api/client/wishlists/price-alerts', {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrftoken
        },
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(
          errorData.message || 'Could not create price alert'
        );
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(
        'An error occurred while creating the price alert'
      );
    }
  }
);

export const deletePriceAlert = createAsyncThunk(
  'priceAlert/delete',
  async (alertId: number, { rejectWithValue }) => {
    const csrftoken = getCookie('csrftoken');
    try {
      const response = await fetch(
        `/api/client/wishlists/price-alerts/${alertId}`,
        {
          method: 'DELETE',
          headers: {
            'X-CSRFToken': csrftoken
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(
          errorData.message || 'Could not delete price alert'
        );
      }

      return alertId; // Return the ID of the deleted alert
    } catch (error) {
      return rejectWithValue(
        'An error occurred while deleting the price alert'
      );
    }
  }
);

export const fetchPriceAlerts = createAsyncThunk(
  'priceAlert/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const csrftoken = getCookie('csrftoken');

      const response = await fetch('/api/client/wishlists/price-alerts', {
        headers: {
          Accept: 'application/json',
          'X-CSRFToken': csrftoken
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(
          errorData.message || 'Could not fetch price alerts'
        );
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue('An error occurred while fetching price alerts');
    }
  }
);

const priceAlertSlice = createSlice({
  name: 'priceAlert',
  initialState,
  reducers: {
    clearPriceAlerts(state) {
      state.alerts = null;
      state.isSuccess = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPriceAlert.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.error = null;
      })
      .addCase(createPriceAlert.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        // Set the alerts to the response payload
        state.alerts = action.payload;
      })
      .addCase(createPriceAlert.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deletePriceAlert.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.error = null;
      })
      .addCase(deletePriceAlert.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Remove the deleted alert from the alerts list
        if (state.alerts) {
          state.alerts.results = state.alerts.results.filter(
            (alert) => alert.pk !== action.payload
          );
        }
      })
      .addCase(deletePriceAlert.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchPriceAlerts.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.error = null;
      })
      .addCase(fetchPriceAlerts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.alerts = action.payload;
      })
      .addCase(fetchPriceAlerts.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearPriceAlerts } = priceAlertSlice.actions;

export const selectPriceAlerts = (state: RootState) => state.priceAlert.alerts;
export const selectPriceAlertsLoading = (state: RootState) =>
  state.priceAlert.isLoading;
export const selectPriceAlertsError = (state: RootState) =>
  state.priceAlert.error;
export const selectPriceAlertSuccess = (state: RootState) =>
  state.priceAlert.isSuccess;

export default priceAlertSlice.reducer;
